/**
 * LoginPage — Regional Admin Authentication
 *
 * Select a region and enter password to access the dashboard.
 * Super Admin (Global) option available for full access.
 */

import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_ACQUISITION_API || 'http://localhost:5500';

interface AuthInfo {
    token: string;
    role: string;
    regions: string[];
    label: string;
}

interface LoginPageProps {
    onLogin: (auth: AuthInfo) => void;
}

const REGION_OPTIONS = [
    { key: 'global', label: '🌐 Super Admin (All Regions)', icon: '🔑', color: '#a855f7' },
    { key: 'us', label: '🌎 North America', icon: '🇺🇸', color: '#3b82f6' },
    { key: 'lat', label: '🌎 Latin America', icon: '🌎', color: '#f59e0b' },
    { key: 'eu', label: '🌍 Europe', icon: '🇪🇺', color: '#6366f1' },
    { key: 'asia', label: '🌏 Asia-Pacific & Middle East', icon: '🌏', color: '#ec4899' },
    { key: 'af', label: '🌍 Africa', icon: '🌍', color: '#22c55e' },
];

export function LoginPage({ onLogin }: LoginPageProps) {
    const [selectedRegion, setSelectedRegion] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Try to restore session from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('acq_auth');
        if (saved) {
            try {
                const auth: AuthInfo = JSON.parse(saved);
                // Verify token is still valid
                fetch(`${API}/auth/verify`, {
                    headers: { Authorization: `Bearer ${auth.token}` },
                })
                    .then((r) => r.json())
                    .then((data) => {
                        if (data.valid) onLogin(auth);
                        else localStorage.removeItem('acq_auth');
                    })
                    .catch(() => localStorage.removeItem('acq_auth'));
            } catch {
                localStorage.removeItem('acq_auth');
            }
        }
    }, [onLogin]);

    async function handleLogin(e: React.FormEvent) {
        e.preventDefault();
        if (!selectedRegion || !password) return;

        setLoading(true);
        setError('');

        try {
            const res = await fetch(`${API}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ region: selectedRegion, password }),
            });

            const data = await res.json();

            if (!res.ok) {
                setError(data.error || 'Login failed');
                return;
            }

            const auth: AuthInfo = {
                token: data.token,
                role: data.role,
                regions: data.regions,
                label: data.label,
            };

            localStorage.setItem('acq_auth', JSON.stringify(auth));
            onLogin(auth);
        } catch (err) {
            setError('Connection failed. Is the server running?');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        }}>
            <div style={{
                width: '100%',
                maxWidth: 420,
                padding: 40,
                background: 'rgba(15, 23, 42, 0.8)',
                backdropFilter: 'blur(20px)',
                borderRadius: 20,
                border: '1px solid rgba(99, 102, 241, 0.2)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            }}>
                {/* Logo / Title */}
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{
                        fontSize: 40,
                        marginBottom: 12,
                    }}>🚀</div>
                    <h1 style={{
                        margin: 0,
                        fontSize: 22,
                        fontWeight: 800,
                        color: '#e2e8f0',
                        letterSpacing: '-0.02em',
                    }}>Acquisition Engine</h1>
                    <p style={{
                        margin: '6px 0 0',
                        fontSize: 13,
                        color: '#64748b',
                    }}>Regional Admin Login</p>
                </div>

                <form onSubmit={handleLogin}>
                    {/* Region Selection */}
                    <label style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: 8,
                    }}>Select Region</label>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 8,
                        marginBottom: 20,
                    }}>
                        {REGION_OPTIONS.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                onClick={() => setSelectedRegion(r.key)}
                                style={{
                                    padding: r.key === 'global' ? '12px 14px' : '10px 14px',
                                    background: selectedRegion === r.key ? `${r.color}22` : 'rgba(30, 41, 59, 0.5)',
                                    border: `1.5px solid ${selectedRegion === r.key ? r.color : '#334155'}`,
                                    borderRadius: 10,
                                    color: selectedRegion === r.key ? r.color : '#94a3b8',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    textAlign: 'left',
                                    transition: 'all 0.15s ease',
                                    gridColumn: r.key === 'global' ? '1 / -1' : undefined,
                                }}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>

                    {/* Password */}
                    <label style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: 8,
                    }}>Password</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter admin password"
                        autoFocus
                        style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: '#0a0f1e',
                            border: '1.5px solid #334155',
                            borderRadius: 10,
                            color: '#e2e8f0',
                            fontSize: 14,
                            boxSizing: 'border-box',
                            fontFamily: 'inherit',
                            marginBottom: error ? 8 : 20,
                        }}
                    />

                    {/* Error */}
                    {error && (
                        <p style={{
                            margin: '0 0 16px',
                            padding: '8px 12px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: 8,
                            color: '#f87171',
                            fontSize: 12,
                            fontWeight: 600,
                        }}>⚠️ {error}</p>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loading || !selectedRegion || !password}
                        style={{
                            width: '100%',
                            padding: 14,
                            background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 12,
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: 15,
                            opacity: loading || !selectedRegion || !password ? 0.5 : 1,
                            transition: 'opacity 0.2s',
                        }}
                    >
                        {loading ? '⏳ Authenticating...' : '🔐 Login'}
                    </button>
                </form>
            </div>
        </div>
    );
}
